import { API_URL } from "env";
import { mixGetV1, mixPostV1, mixPostV2 } from "../services/mixin";

export interface WorkLogCreator {
    id: number;
    name: string;
    fullname?: string;
    email: string;
}

/** Ảnh đính kèm của một bản ghi lịch sử công việc (image_path là URL đầy đủ) */
export interface WorkLogPhoto {
    id: number;
    customer_work_log_id?: number;
    image_path: string;
    filename?: string | null;
    created_by_id?: number;
    created_at?: string;
    updated_at?: string;
}

export interface WorkLogItem {
    id: number;
    customer_id: number;
    created_by_id: number;
    content: string;
    created_at: string;
    updated_at: string;
    creator: WorkLogCreator;
    /** Ảnh của bản ghi (API trả về mảng, rỗng nếu không có ảnh) */
    photos?: WorkLogPhoto[] | null;
}

export interface WorkLogListResponse {
    status: boolean;
    data: {
        total: number;
        current_page: number;
        items: WorkLogItem[];
    };
}

export const getCustomerWorkLogs = async (
    customerId: number,
    page = 1,
    limit = 20
): Promise<WorkLogListResponse> => {
    return mixGetV1("/api/v1/be/customer-work-logs", {
        customer_id: customerId,
        page,
        limit,
        sort: "id,desc",
    });
};

export const createCustomerWorkLog = async (
    customerId: number,
    content: string
): Promise<{ status: boolean; data: WorkLogItem; msg?: string }> => {
    return mixPostV1("/api/v1/be/customer-work-logs", {
        customer_id: customerId,
        content,
    });
};

/**
 * Sửa nội dung một bản ghi lịch sử công việc.
 * POST /api/v1/be/customer-work-logs/{id} (body form-data, KHÔNG phải PUT)
 */
export const updateCustomerWorkLog = async (
    id: number,
    content: string
): Promise<{ status: boolean; data?: WorkLogItem; msg?: string; error?: any }> => {
    return mixPostV2(`/api/v1/be/customer-work-logs/${id}`, { content });
};

/** Xóa một bản ghi lịch sử công việc. */
export async function deleteCustomerWorkLog(
    id: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/customer-work-logs/${id}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Ảnh ghi chú khách hàng (type = photo_note) — hiển thị dưới ô nhập lịch sử công việc.
 *  - GET    /api/v1/be/customers/{id}/photos-note
 *  - POST   /api/v1/be/customers/{id}/photos-note   (multipart photo_note[], ≤10MB/ảnh)
 *  - DELETE /api/v1/be/customers/photos-note/{photo_id}
 */

export interface CustomerNotePhoto {
    id: number;
    customer_id: number;
    image: string;
    title?: string | null;
    type?: string;
    created_at?: string;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export async function getCustomerNotePhotos(
    customerId: number
): Promise<{ status: boolean; items: CustomerNotePhoto[] }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/customers/${customerId}/photos-note`, {
            headers: authHeaders(),
        });
        const json = await res.json();
        const d = json.data;
        return { status: !!json.status, items: Array.isArray(d) ? d : (d?.items || []) };
    } catch {
        return { status: false, items: [] };
    }
}

export async function uploadCustomerNotePhotos(
    customerId: number,
    files: File[]
): Promise<{ status: boolean; data?: CustomerNotePhoto[]; msg?: string }> {
    try {
        const fd = new FormData();
        files.forEach((f) => fd.append("photo_note[]", f));
        const res = await fetch(`${API_URL}/api/v1/be/customers/${customerId}/photos-note`, {
            method: "POST",
            headers: authHeaders(),
            body: fd,
        });
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function deleteCustomerNotePhoto(
    photoId: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/customers/photos-note/${photoId}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}
